/**
 * AI Service with Gemini as primary and OpenRouter as fallback
 * Handles communication with AI services
 */

// Primary AI: Google Gemini
const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY;
const GEMINI_MODEL = 'gemini-2.5-flash';
const GEMINI_API_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent';

// Fallback AI: OpenRouter
const OPENROUTER_API_KEY = import.meta.env.VITE_OPENROUTER_API_KEY;
const OPENROUTER_MODEL = 'openai/gpt-oss-20b:free';
const OPENROUTER_API_URL = 'https://openrouter.ai/api/v1/chat/completions';

// Company context for the AI
const COMPANY_CONTEXT = `
You are Valora, an AI assistant for Real Value Group, a data analytics and business intelligence consultancy.

About Real Value Group:
- We specialize in transforming raw data into actionable business insights
- Our core services include:
  * Data Analytics & Business Intelligence
  * Custom Dashboard Development
  * Data Strategy Consulting
  * ETL Pipeline Development
  * Data Visualization
  * Predictive Analytics
  * Data Governance & Quality Management

- Our LinkedIn page: https://www.linkedin.com/company/realvaluegroup/
- Contact email: info@realvaluegroup.io
- Office location: Bergstraat 22, 3110 Rotselaar, Belgium
- Location map link: https://maps.app.goo.gl/aU1SC19SGFDBd6jB8

Our Mission:
We help organizations unlock the full potential of their data by providing comprehensive analytics solutions that drive informed decision-making and business growth.

Our Approach:
1. Understand your business objectives
2. Assess your current data landscape
3. Design tailored analytics solutions
4. Implement robust data infrastructure
5. Deliver actionable insights through intuitive dashboards
6. Provide ongoing support and optimization

Key Differentiators:
- Expert team with deep industry experience
- Custom solutions tailored to your specific needs
- Focus on ROI and measurable business outcomes
- End-to-end service from strategy to implementation
- Commitment to data quality and governance

When responding:
- Your name is Valora and you may introduce yourself when appropriate
- Be helpful, professional, and friendly
- When asked about our address or location, always include: Our office is located at Bergstraat 22/303, 3110 Rotselaar, Belgium. Then mention: You can check our location via the map link: https://maps.app.goo.gl/aU1SC19SGFDBd6jB8
- Use natural, conversational language only
- DO NOT use any markdown formatting, bold, italics, or special characters
- DO NOT use asterisks, underscores, hashtags, or any formatting symbols
- DO NOT create lists with bullets, dashes, or numbers
- DO NOT use tables or structured formatting
- Write in plain text paragraphs as if speaking naturally to someone
- Answer questions about our services, approach, and capabilities
- If asked about specific pricing or detailed proposals, suggest contacting us directly
- Encourage users to reach out via our contact form or email for personalized consultations
- Stay focused on Real Value Group's services and expertise
`;

/**
 * Try to get response from Gemini AI
 * @param {Array} messages - Array of message objects with role and content
 * @returns {Promise<string>} AI response
 */
async function callGemini(messages) {
  // Build conversation history for Gemini
  let conversationText = COMPANY_CONTEXT + '\n\n';

  messages.forEach(msg => {
    if (msg.role === 'user') {
      conversationText += `User: ${msg.content}\n`;
    } else if (msg.role === 'assistant') {
      conversationText += `Valora: ${msg.content}\n`;
    }
  });

  conversationText += 'Valora:';

  const response = await fetch(`${GEMINI_API_URL}?key=${GEMINI_API_KEY}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      contents: [{
        parts: [{
          text: conversationText
        }]
      }],
      generationConfig: {
        temperature: 0.7,
        maxOutputTokens: 500,
      }
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || `Gemini API failed with status ${response.status}`);
  }

  const data = await response.json();

  if (!data.candidates || !data.candidates[0] || !data.candidates[0].content || !data.candidates[0].content.parts || !data.candidates[0].content.parts[0]) {
    throw new Error('Invalid response format from Gemini');
  }

  return data.candidates[0].content.parts[0].text;
}

/**
 * Try to get response from OpenRouter AI (fallback)
 * @param {Array} messages - Array of message objects with role and content
 * @returns {Promise<string>} AI response
 */
async function callOpenRouter(messages) {
  const messagesWithContext = [
    {
      role: 'system',
      content: COMPANY_CONTEXT,
    },
    ...messages.map((msg) => ({
      role: msg.role,
      content: msg.content,
    })),
  ];

  const response = await fetch(OPENROUTER_API_URL, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${OPENROUTER_API_KEY}`,
      'Content-Type': 'application/json',
      'HTTP-Referer': window.location.origin,
      'X-Title': 'Real Value Group Website',
    },
    body: JSON.stringify({
      model: OPENROUTER_MODEL,
      messages: messagesWithContext,
      temperature: 0.7,
      max_tokens: 500,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || `OpenRouter API failed with status ${response.status}`);
  }

  const data = await response.json();

  if (!data.choices || !data.choices[0] || !data.choices[0].message) {
    throw new Error('Invalid response format from OpenRouter');
  }

  return data.choices[0].message.content;
}

/**
 * Chat with AI - tries Gemini first, falls back to OpenRouter
 * @param {Array} messages - Array of message objects with role and content
 * @returns {Promise<string>} AI response
 */
export async function chatWithAI(messages) {
  // Try Gemini first
  try {
    console.log('Trying Gemini AI...');
    const response = await callGemini(messages);
    console.log('Gemini AI responded successfully');
    return response;
  } catch (geminiError) {
    console.warn('Gemini AI failed, trying OpenRouter fallback...', geminiError);

    // Fallback to OpenRouter
    try {
      const response = await callOpenRouter(messages);
      console.log('OpenRouter AI responded successfully (fallback)');
      return response;
    } catch (openRouterError) {
      console.error('Both AI services failed:', { geminiError, openRouterError });
      throw new Error('Unable to connect to AI services. Please try again.');
    }
  }
}

